import { ModalManager } from './manager';
import { Auth } from '../auth';
import { escapeHtml } from '../utils';
import { t, withLocale } from '../i18n';

/**
 * Mock Google account picker. Visual cousin of the real Google Identity
 * Services prompt — used in dev until B-006 wires `google.accounts.id` for
 * real. The real flow will: load GIS script, call `initialize({client_id})`,
 * call `prompt()`, receive an `id_token`, POST to `/api/auth/google`,
 * receive a session cookie. This mock skips all of that and just calls
 * `Auth.loginDev(...)` with a synthetic user.
 *
 * Replacement target: see B-006 acceptance in docs/backlog.md.
 */

const TITLE_ID = 'google-picker-title';

const MOCK_ACCOUNTS = [
  { id: 'g-1', name: 'Agent Claude', email: 'roamzy.agent@gmail.com', initials: 'AC' },
];

export function openGooglePicker(opts: { redirectTo?: string } = {}): void {
  ModalManager.open({
    id: 'google-picker',
    panelClass: 'google-picker',
    labelledBy: TITLE_ID,
    render() {
      return `
<div class="google-picker__brand">
  <span class="google-g" aria-hidden="true">G</span>
  <span class="google-picker__brand-text">${t('auth.googleSignIn')}</span>
</div>
<h2 id="${TITLE_ID}" class="google-picker__title">${t('auth.googleChooseAccount')}</h2>
<p class="google-picker__sub">${t('auth.googleChooseSub')} <strong>roamzy.io</strong></p>
<ul class="google-picker__accounts">
  ${MOCK_ACCOUNTS.map(
    (a) => `<li>
    <button class="google-account" data-account-id="${a.id}">
      <span class="google-account__avatar" aria-hidden="true">${escapeHtml(a.initials)}</span>
      <span class="google-account__name">${escapeHtml(a.name)}</span>
      <span class="google-account__email">${escapeHtml(a.email)}</span>
    </button>
  </li>`,
  ).join('')}
  <li>
    <button class="google-account google-account--add" data-account-id="add">
      <span class="google-account__avatar" aria-hidden="true">+</span>
      <span class="google-account__name">${t('auth.googleUseAnother')}</span>
    </button>
  </li>
</ul>
<p class="google-picker__footer">${t('auth.googleMockFooter')}</p>`;
    },
    onMount(panel) {
      const onClick = (e: Event): void => {
        const btn = (e.target as Element | null)?.closest<HTMLElement>('[data-account-id]');
        if (!btn) return;
        const id = btn.dataset.accountId;
        if (id === 'add') {
          // Real flow would re-trigger GIS with a fresh prompt.
          return;
        }
        const account = MOCK_ACCOUNTS.find((a) => a.id === id);
        if (!account) return;
        ModalManager.close('google-picker');
        void Auth.loginDev(`google-${account.id}`).then(() => {
          if (opts.redirectTo) {
            window.history.pushState({}, '', withLocale(opts.redirectTo));
            window.dispatchEvent(new PopStateEvent('popstate'));
          }
        });
      };
      panel.addEventListener('click', onClick);
      return () => panel.removeEventListener('click', onClick);
    },
  });
}
